import { Component, inject, Input, OnDestroy, OnInit, TemplateRef } from '@angular/core'
import { Store } from '@ngrx/store'
import {
  IncomingPayoutAgreement,
  PayoutAgreement,
  PayoutData,
  PayoutFrequency
} from '../../../../store/payout-agreement/payout-agreement.state'
import { splitUnderscoreAndCapitalize } from '../../../../utils/payout-agreement.utils'
import {
  selectAllPayouts,
  selectPayout
} from '../../../../store/payout-agreement/payout-agreement.reducers'
import { filter, map, Observable, Subject, take, takeUntil, tap } from 'rxjs'
import { CommonModule, NgIf } from '@angular/common'
import {
  ExtendedSalaryComponent,
  StatutoryCompliances
} from '../../../../store/employee-salary/employee-salary.state'
import { HasPermissionDirective } from '../../../../directives/has-permission.directive'
import { eps } from '../../../../services/url.service'
import { RouterModule } from '@angular/router'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { loadEmployeeById } from '../../../../store/user/user.actions'
import { User } from '../../../../store/auth/auth.state'
import { selectEmployeeDetail } from '../../../../store/user/user.selectors'
import { payoutAgreementActions } from '../../../../store/payout-agreement/payout-agreement.actions'
import { CapitalizePipe } from '../../../../pipes/capitalize.pipe'

type PayoutDataEntry = [string, number | ExtendedSalaryComponent[]]

@Component({
  selector: 'app-payout-tab',
  imports: [NgIf, CommonModule, HasPermissionDirective, RouterModule, CapitalizePipe],
  templateUrl: './payout-tab.component.html',
  styleUrl: './payout-tab.component.scss'
})
export class PayoutTabComponent implements OnInit, OnDestroy {
  store = inject(Store)
  modalService = inject(NgbModal)
  eps = eps //EndPoints from URL Service
  @Input() employeeId!: string
  private destroyed$ = new Subject<void>()
  allPayoutAgreements$: Observable<PayoutAgreement[] | null> = this.store.select(selectAllPayouts)
  payoutFrequency: PayoutFrequency | null = null
  ctc: number | null = null
  currency: string | null = null
  basis: string | null = null
  payout: PayoutAgreement | null = null
  salaryCompliancesList: StatutoryCompliances | null = null
  payoutDataArr: any = []
  formatEnumValues = splitUnderscoreAndCapitalize
  employee$: Observable<User> = this.store.select(selectEmployeeDetail)

  ngOnInit(): void {
    this.store.dispatch(loadEmployeeById({ id: this.employeeId }))
    this.store
      .select(selectPayout)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(payoutAgreement => {
        if (payoutAgreement) {
          // This is your existing logic for when data is present
          this.payoutFrequency = payoutAgreement.payoutFrequency ?? null
          this.salaryCompliancesList = payoutAgreement.statutoryCompliances ?? null
          this.ctc = payoutAgreement.ctc ?? null
          this.currency = payoutAgreement.currency ?? null
          this.basis = payoutAgreement.basis ?? null
          this.payout = payoutAgreement

          this.payoutDataArr = (
            Object.entries(payoutAgreement.payoutData || {}) as [
              keyof PayoutData,
              PayoutData[keyof PayoutData]
            ][]
          )
            .filter(([key, value]) => !(key === 'pf' && value === 0))
            .map<PayoutDataEntry>(([key, value]) => {
              if (['hra'].includes(key)) {
                key = key.toUpperCase() as keyof PayoutData
              } else if (key === 'specialAllowance') {
                key = 'Special Allowances' as keyof PayoutData
              } else if (key === 'pf') {
                key = "Employee's PF Contribution" as keyof PayoutData
              } else if (key === 'basic') {
                key = 'Basic' as keyof PayoutData
              }
              return [key, value]
            })
            .sort((a, b) => a[0].localeCompare(b[0]))
        } else {
          // THIS IS THE FIX: Explicitly clear component properties when the state is null
          this.payoutFrequency = null
          this.salaryCompliancesList = null
          this.ctc = null
          this.currency = null
          this.basis = null
          this.payout = null
          this.payoutDataArr = [] // Reset the array to clear the view
        }
      })

    this.employee$
      .pipe(
        filter(employee => !!employee),
        tap(employee => {
          if (employee) {
            this.store.dispatch(
              payoutAgreementActions.loadPayoutAgreement({ employeeId: this.employeeId })
            )
            this.store.dispatch(
              payoutAgreementActions.loadAllPayoutAgreements({ employeeId: this.employeeId })
            )
          }
        })
      )
      .subscribe()
  }

  ngOnDestroy(): void {
    this.destroyed$.next()
    this.destroyed$.complete()
  }

  //Helper method to check if the employee has any salary compliances
  hasValidCompliances(): boolean {
    if (!this.salaryCompliancesList) return false
    return (
      this.salaryCompliancesList &&
      Object.keys(this.salaryCompliancesList).some(
        key =>
          key !== 'employerCapping' &&
          this.salaryCompliancesList?.[key as keyof StatutoryCompliances] === true
      )
    )
  }

  openBusinessModal(content: TemplateRef<any>, options: any = {}) {
    this.modalService.open(content, {
      scrollable: false,
      centered: true,
      size: 'lg',
      ...options
    })
  }

  onClose() {
    this.modalService.dismissAll()
  }
}
